import {Dispatch, useCallback, useMemo, useState} from "react";
import {getTestMode, getTestTypeId, TestType} from "../../../../shared/types/testTypes";
import {IFormCreateTestTraining} from "../components/_interfaces";
import {
    API_BY_TYPE,
    extractClassroomData,
    findMatch, getSuccessMessage,
    isNotValidClassroomCheck, resolveItemsNumber,
    resolveSelectedGrade,
    resolveSubcategories
} from "../utils/functions";
import moment from "moment";
import {useServerErrors} from "../../../../hooks/useServerErrors";
import {UseFormClearErrors, UseFormSetError} from "react-hook-form";
import {useAuth} from "../../../../hooks/useAuth";
import {APICreateTestCustomManny, APICreateTestManny} from "../api/createTestMultipleClassrooms";
import {APICreateTest} from "../api/createTest";

interface UseTestFormSubmitOptions {
    type: TestType;
    multipleClassrooms: boolean;
    state: IFormCreateTestTraining;
    dispatch: Dispatch<any>;
    items: any[],
    setError?: UseFormSetError<any>;
    clearErrors?: UseFormClearErrors<any>;
    handleStepTransition: () => boolean;
}

const useTestSubmission = ({
                                     type,
                                     state,
                                     multipleClassrooms = false,
                                     items,
                                     setError,
                                     clearErrors,
                                     handleStepTransition} : UseTestFormSubmitOptions) => {
    const { update } = useAuth();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const { handleServerErrors } = useServerErrors(setError ?? (() => {}));
    const [_isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const isFormValid = useMemo(() => {
        const hasSelectedUsers = state.users.some((user: any) => Boolean(user.checked));
        if (!hasSelectedUsers && !multipleClassrooms) return false;

        const hasValidClassroom = multipleClassrooms && !isNotValidClassroomCheck(state.users);

        const hasCategoriesOrVariant = Boolean(state.variant.value) || findMatch(true, state.categories);
        const hasDuration = type === "school" ? Boolean(state.test_duration?.value) : Boolean(state.custom_duration);

        const validators: Record<string, () => boolean> = {
            school: () => hasCategoriesOrVariant && hasDuration,
            numericLiteracy: () => Boolean(state.subject.value),
            training: () => hasCategoriesOrVariant && Boolean(state.items_number.value) && hasDuration,
            customTest: () => Boolean(state.grade.value),
        };

        if(multipleClassrooms && type === "school")
            return hasValidClassroom && hasCategoriesOrVariant;

        if(multipleClassrooms && type === "customTest")
            return hasValidClassroom && Boolean(state.grade.value);

        return validators[type] ? validators[type]() : hasCategoriesOrVariant;
    }, [
        type,
        state.users,
        state.categories,
        state.variant.value,
        state.test_duration?.value,
        state.custom_duration,
        state.subject.value,
        state.items_number.value,
        state.grade.value,
    ]);

    const buildPayload = useCallback(() => {
        const isSchool = type === "school";
        const isBac = type === "bac";
        const isEvaluate = type === "evaluate";
        const isTraining = type === "training";
        const isCustomTest = type === "customTest";
        const isDigitalLiteracy = type === "digitalLiteracy";
        const isNumericLiteracy = type === "numericLiteracy";
        const isMultipleClassroomsSchool = type === "school" && multipleClassrooms;

        const classroomsData = multipleClassrooms && extractClassroomData(state.users);
        const selectedStudents = state.users
            .filter((user: { checked?: boolean }) => Boolean(user.checked))
            .map((user: any) => user.id);
        const hasStandard = Number(state.grade.value) <= 8;
        const selectedItems = items.map(({ id }: { id: number }) => id);
        const subcategories = resolveSubcategories(type, items, state.grade.value, state.categories);
        const grade = resolveSelectedGrade(type, state.grade);
        const itemTypeId = isSchool || isCustomTest ? state.subject.value : getTestTypeId(type);
        const testMode = isBac || isEvaluate ? state.subject.value : getTestMode(type);
        const itemsNumber = resolveItemsNumber(type, state.test_duration.value, state.items_number.value);

        const basePayload = {
            grade,
            subcategories,
            test_config: state.test_config.value,
            start_at: moment(state.start_date).tz("Europe/Bucharest").locale("ro").format("YYYY-MM-DD HH:mm:ss"),
            expire: state.expire,
            tag: state.tag.value,
            item_type_id: itemTypeId,
        };

        return {
            ...basePayload,
            ...((multipleClassrooms || isSchool || isBac || isEvaluate || isTraining) && {individual_test: state.individual_test.value === "1",}),
            ...(!isCustomTest && {test_mode: testMode,}),
            ...((isCustomTest || isTraining) && {
                duration: state.custom_duration,
                items: selectedItems,
            }),
            ...((isDigitalLiteracy || isNumericLiteracy) && {grade_year: state.grade.value,}),
            ...((isSchool || isTraining || multipleClassrooms) && {items_number: itemsNumber, has_standard: hasStandard}),
            ...(isEvaluate && {test_variant: state.variant.value !== "" ? state.variant.value : null,}),
            ...(isNumericLiteracy && {test_type: state.subject.value,}),
            ...(isTraining && {difficulty: state.difficulty.value,}),
            ...(!multipleClassrooms && {
                users: selectedStudents,
                classroom_id: state.classroom?.value,}),
            ...(multipleClassrooms && {classrooms: classroomsData,}),
            ...(isMultipleClassroomsSchool && {items: state.items_ids,})
        };
    }, [state, type, items]);

    const submitTest = useCallback(async () => {
        const multipleClassroomsSchool = multipleClassrooms && type === "school";
        const multipleClassroomsCustomTest = multipleClassrooms && type === "customTest";

        clearErrors?.();
        setErrorMessage("");

        if (handleStepTransition()) {
            return false;
        }
        setIsSubmitting(true);
        update("loading", { show: true });

        const apiCall = multipleClassroomsSchool ? APICreateTestManny : multipleClassroomsCustomTest ? APICreateTestCustomManny : API_BY_TYPE[type] ?? APICreateTest;

        try {
            const payload = buildPayload();
            const response = await apiCall(payload);

            if (response.data.success) {
                setSuccessMessage(getSuccessMessage(type));
                return true;
            }

            update("error", { show: true, code: "A002" });
            return false;
        } catch (err: any) {
            if (err.response?.status === 400) {
                setErrorMessage("Unul sau mai multe câmpuri nu sunt valide.");
                const errors = err.response.data?.data;
                if (errors) {
                    handleServerErrors(errors, { start_at: "start_date" });
                    if (errors.credits === 0 && errors.message) {
                        setErrorMessage(errors.message);
                    }
                }
            } else {
                update("error", { show: true, code: "A001" });
            }
            return false;
        } finally {
            setIsSubmitting(false);
            update("loading", { show: false });
        }
    }, [buildPayload,
        clearErrors,
        handleServerErrors,
        handleStepTransition,
        type,
        update
    ]);

    return {
        errorMessage,
        setErrorMessage,
        successMessage,
        setSuccessMessage,
        isFormValid,
        submitTest
    }
}

export default useTestSubmission;